import { isArray } from '@abp/ng.core';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ObjectHelper } from 'src/core/helpers/object.helper';
import { AttachedDataFilterDto } from 'src/core/models/administration/attached-data/attached-data-filter.dto';
import { AttachedDataLookupDto } from 'src/core/models/administration/attached-data/attached-data-lookup.dto';
import { AttachedDataMultipleFilterDto } from 'src/core/models/administration/attached-data/attached-data-multiple-filter.dto';
import { AttachedDataMultipleValueModel } from 'src/core/models/administration/attached-data/attached-data-multiple-value.model';
import { AttachedDataDto } from 'src/core/models/administration/attached-data/attached-data.dto';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { AttachedDataService } from 'src/core/services/administration/attached-data.service';
import { CrudService } from 'src/core/services/crud/crud.service';

@Component({
    selector: 'ca-attached-data-multiple-selector',
    templateUrl: './attached-data-multiple-selector.component.html',
    styleUrls: ['./attached-data-multiple-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AttachedDataMultipleSelectorComponent),
        },
    ],
    standalone: false
})
export class AttachedDataMultipleSelectorComponent implements OnInit, ControlValueAccessor {
  @Input()
  maxSelectedItems: number;

  @Input()
  set filters(value: FilterItemDto[]) {
    if (!value) {
      value = [];
    }

    const visibleFilterIndex = value.findIndex(f => f.field === 'visible');
    if (visibleFilterIndex === -1) {
      value = [
        ...value,
        {
          field: 'visible',
          value: true,
          operator: this.operators.Equals,
        },
      ];
    }

    this._filters = value;
    if (this.filters) {
      this.load();
    }
  }

  get filters(): FilterItemDto[] {
    return this._filters ?? [];
  }

  selectedMultipleValueList: AttachedDataMultipleValueModel[] = [];
  multipleValueList: AttachedDataMultipleValueModel[] = [];
  totalCount: number;
  _filters: FilterItemDto[] = [];

  set value(val: AttachedDataMultipleFilterDto) {
    this._value = val;
    this.setValues(val);
  }

  get value(): AttachedDataMultipleFilterDto {
    return this._value;
  }

  private _value: AttachedDataMultipleFilterDto = { filters: [] };

  constructor(
    private service: CrudService,
    private attachedDataService: AttachedDataService,
    private operators: Operators
  ) {}

  ngOnInit(): void {
    this.load();
  }

  writeValue(obj: any): void {
    obj = obj ? obj : ({ filters: [] } as AttachedDataMultipleFilterDto);
    this.value = obj;
    this.setValues(obj);
  }

  onChange(selection: any) {}

  onTouched() {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  applyChanges(): void {
    this.onChange(this.getAttachedDataMultipleFilters(this.selectedMultipleValueList));
  }

  clearSelection(): void {
    this.multipleValueList.forEach(
      x =>
        (x.values = x.attachedData.isMultipleChoice
          ? []
          : [
              {
                value: '',
                deletable: true,
              },
            ])
    );
    this.selectedMultipleValueList = [];
  }

  customSearchFn(term: string, item: AttachedDataMultipleValueModel): boolean {
    term = term.toLocaleLowerCase();
    return item.attachedData.name.toLocaleLowerCase().indexOf(term) > -1;
  }

  onItemSelect(eventArgs): void {
    if (isArray(eventArgs)) {
      this.selectedMultipleValueList = eventArgs;
      eventArgs.forEach(a => {
        if (
          a.attachedData.isMultipleChoice &&
          (!a.attachedData.attachedDataLookups || a.attachedData.attachedDataLookups.length <= 0)
        ) {
          this.attachedDataService
            .getAttachedDataLookups(a.attachedData.id)
            .pipe(take(1))
            .subscribe(response => {
              a.attachedData.attachedDataLookups = response;
            });
        }
      });

      this.applyChanges();
    }
  }

  onDeleteClick(selection: AttachedDataMultipleValueModel): void {
    if (selection) {
      this.selectedMultipleValueList = this.selectedMultipleValueList.filter(
        x => x.attachedData.id !== selection.attachedData.id
      );
      this.applyChanges();
    }
  }

  isSelected(value: AttachedDataMultipleValueModel): boolean {
    var selected = this.selectedMultipleValueList.filter(
      x => x.attachedData.id === value.attachedData.id
    );
    if (selected.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  onItemValueChange(selectedValue: AttachedDataMultipleValueModel, eventArgs): void {
    if (selectedValue) {
      selectedValue.values[0].value = eventArgs.target.value;

      this.applyChanges();
    }
  }

  isValueSelected(
    selectedValue: AttachedDataMultipleValueModel,
    value: AttachedDataLookupDto
  ): boolean {
    var selected = selectedValue.values.filter(x => x.value === value.value);
    if (selected.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  load() {
    this.service
      .get<AttachedDataDto>(AttachedDataDto, {
        maxResultCount: 9999,
        skipCount: 0,
        filters: this.filters,
        sorters: [],
      })
      .pipe(take(1))
      .subscribe(response => {
        this.totalCount = response.totalCount;

        this.multipleValueList = response.items?.map(a => {
          return {
            attachedData: a,
            values: a.isMultipleChoice
              ? []
              : [
                  {
                    value: '',
                    deletable: true,
                  },
                ],
          } as AttachedDataMultipleValueModel;
        });

        this.setValues(this.value);
      });
  }

  private getAttachedDataMultipleFilters(
    multipleValueList: AttachedDataMultipleValueModel[]
  ): AttachedDataMultipleFilterDto {
    const tempMultipleValueList = ObjectHelper.deepCopy(multipleValueList);

    var newList: AttachedDataFilterDto[] = [];

    tempMultipleValueList.forEach(ad => {
      if (ad?.values?.length > 0) {
        ad?.values?.forEach(adv => {
          newList.push({
            id: ad.attachedData.id,
            name: ad.attachedData.name,
            value: adv.value,
          } as AttachedDataFilterDto);
        });
      } else {
        newList.push({
          id: ad.attachedData.id,
          name: ad.attachedData.name,
          value: '',
        } as AttachedDataFilterDto);
      }
    });

    return { filters: newList } as AttachedDataMultipleFilterDto;
  }

  private getAttachedDataMultipleValueList(
    attachedDataMultipleFilterDto: AttachedDataMultipleFilterDto
  ): AttachedDataMultipleValueModel[] {
    var newList: AttachedDataMultipleValueModel[] = [];

    if (attachedDataMultipleFilterDto && attachedDataMultipleFilterDto.filters) {
      const tempValueList = ObjectHelper.deepCopy(attachedDataMultipleFilterDto.filters);

      var resultMap = (resultMap = tempValueList?.reduce((r, a) => {
        r[a.id] = r[a.id] || [];
        r[a.id].push(a);
        return r;
      }, new Map<number, AttachedDataFilterDto[]>()));

      Object.entries<AttachedDataFilterDto[]>(resultMap)?.forEach(([key, values]) => {
        const admv = this.multipleValueList.find(x => key === x.attachedData.id.toString());

        if (admv) {
          newList.push(admv);

          if (admv.attachedData.isMultipleChoice) {
            if (
              !admv.attachedData.attachedDataLookups ||
              admv.attachedData.attachedDataLookups.length <= 0
            ) {
              this.attachedDataService
                .getAttachedDataLookups(admv.attachedData.id)
                .pipe(take(1))
                .subscribe(response => {
                  admv.attachedData.attachedDataLookups = response;
                  admv.values = admv.attachedData.attachedDataLookups.filter(x =>
                    values.map(k => k.value).includes(x.value)
                  );
                });
            } else {
              admv.values = admv.attachedData.attachedDataLookups.filter(x =>
                values.map(k => k.value).includes(x.value)
              );
            }
          } else {
            values.forEach(f => {
              admv.values = [
                {
                  value: f.value,
                  deletable: true,
                } as AttachedDataLookupDto,
              ];
            });
          }
        }
      });
    }

    return newList;
  }

  private setValues(values: AttachedDataMultipleFilterDto): void {
    this.clearSelection();
    if (values && values.filters.length > 0) {
      this.selectedMultipleValueList = this.getAttachedDataMultipleValueList(values);
    }
  }
}
